import Vue from 'vue';
import drivingPracticeExamService from '@/api/driving-practice-exam-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

export const getDefaultDrivingPracticeExamFormItem = () => ({
  date: '',
});

export const getDefaultDrivingPracticeExamFilterParams = () => ({
  show: 'upcoming',
});

const state = {
  drivingPracticeExams: [],
  drivingPracticeExamPagination: {},
  editedDrivingPracticeExam: {},
  newDrivingPracticeExam: getDefaultDrivingPracticeExamFormItem(),
  drivingPracticeExamValidationErrors: {},
  drivingPracticeExamFilterParams: getDefaultDrivingPracticeExamFilterParams(),
  availableExamDatesByAirport: [],
};

const getters = {};

const mutations = {
  SET_DRIVING_PRACTICE_EXAMS(state, { data, current_page, per_page, total }) {
    state.drivingPracticeExams = data;
    state.drivingPracticeExamPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_DRIVING_PRACTICE_EXAM_FILTER_PARAMS(state, params) {
    state.drivingPracticeExamFilterParams = params;
  },

  SET_EDITED_DRIVING_PRACTICE_EXAM(state, drivingPracticeExam) {
    state.drivingPracticeExamValidationErrors = {};
    state.editedDrivingPracticeExam = JSON.parse(JSON.stringify(drivingPracticeExam));
  },

  STORE_DRIVING_PRACTICE_EXAM(state, drivingPracticeExam) {
    state.drivingPracticeExams.push(drivingPracticeExam);
    state.drivingPracticeExamPagination.total += 1;
    state.drivingPracticeExamValidationErrors = {};
    state.newDrivingPracticeExam = getDefaultDrivingPracticeExamFormItem();
  },

  UPDATE_DRIVING_PRACTICE_EXAM(state, drivingPracticeExam) {
    state.drivingPracticeExams = updateArrayItem(state.drivingPracticeExams, drivingPracticeExam);
  },

  DELETE_DRIVING_PRACTICE_EXAM(state, drivingPracticeExam) {
    state.drivingPracticeExams = removeArrayItem(state.drivingPracticeExams, drivingPracticeExam);
    state.drivingPracticeExamPagination.total -= 1;
  },

  SET_DRIVING_PRACTICE_EXAM_VALIDATION_ERRORS(state, drivingPracticeExamValidationErrors) {
    state.drivingPracticeExamValidationErrors = drivingPracticeExamValidationErrors;
  },

  CLEAR_DRIVING_PRACTICE_EXAM_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.drivingPracticeExamValidationErrors, field);
  },

  SET_AVAILABLE_DATES_BY_AIRPORT(state, dates) {
    state.availableExamDatesByAirport = dates;
  },
};

const actions = {
  fetchDrivingPracticeExams({ commit }, params) {
    commit('SET_DRIVING_PRACTICE_EXAM_FILTER_PARAMS', params);
    return drivingPracticeExamService.getPage(params).then((res) => {
      commit('SET_DRIVING_PRACTICE_EXAMS', res.data);
      return res.data;
    });
  },

  storeDrivingPracticeExam({ commit }, drivingPracticeExam) {
    return drivingPracticeExamService
      .create(drivingPracticeExam)
      .then((res) => {
        commit('STORE_DRIVING_PRACTICE_EXAM', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_practice_exam_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_DRIVING_PRACTICE_EXAM_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editDrivingPracticeExam({ state, commit }, drivingPracticeExamId) {
    const drivingPracticeExam = state.drivingPracticeExams?.find(
      (c) => c.id === drivingPracticeExamId,
    );
    if (drivingPracticeExam) {
      commit('SET_EDITED_DRIVING_PRACTICE_EXAM', drivingPracticeExam);
      return Promise.resolve(drivingPracticeExam);
    }
    return drivingPracticeExamService.getById(drivingPracticeExamId).then((res) => {
      commit('SET_EDITED_DRIVING_PRACTICE_EXAM', res.data);
      return res.data;
    });
  },

  updateDrivingPracticeExam({ commit }, drivingPracticeExam) {
    return drivingPracticeExamService
      .update(drivingPracticeExam)
      .then((res) => {
        commit('UPDATE_DRIVING_PRACTICE_EXAM', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_practice_exam_updated'));
        return res.data;
      })
      .catch((err) => {
        if (err.response && err.response.status && err.response.status === 409) {
          eventBus.$emit(OPEN_SNACKBAR, err.response.data);
          throw err;
        }
        commit('SET_DRIVING_PRACTICE_EXAM_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteDrivingPracticeExam({ commit }, drivingPracticeExam) {
    openConfirmDialog({
      title: i18n.t('confirm_driving_practice_exam_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      drivingPracticeExamService.delete(drivingPracticeExam).then(() => {
        commit('DELETE_DRIVING_PRACTICE_EXAM', drivingPracticeExam);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('driving_practice_exam_deleted'));
      });
    });
  },

  getAvailableDatesByAirport({ commit }, airportId) {
    return drivingPracticeExamService.getAvailableDates(airportId).then((res) => {
      commit('SET_AVAILABLE_DATES_BY_AIRPORT', res.data);
      return res.data;
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
